import { useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { type Place, type PlaceDetails, useGooglePlaces } from 'GooglePlaces';
import { type LocationSearchProps } from './LocationSearch.types';

// A place-search field backed by Google Places autocomplete: the themed search input plus the hook
// wiring, so callers only supply what to do with the chosen place.
//
// The visual treatment matches the app's other search fields — the wrapper carries the border and
// background, and the input inside is transparent with no outline, so focusing it doesn't draw the
// browser's default highlight.
export function LocationSearch({
    placeholder,
    'aria-label': ariaLabel,
    onPlaceChanged,
    scope = 'cities',
    clearOnSelect = false,
}: LocationSearchProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);

    // An empty `types` list is how Google is told to offer full street addresses; the default is
    // cities only.
    const autoCompleteOptions = useMemo(() => ({ types: scope === 'cities' ? ['(cities)'] : [] }), [scope]);

    const handlePlaceChanged = useCallback(
        (place: Place, placeDetails: PlaceDetails) => {
            onPlaceChanged(place, placeDetails);
            if (clearOnSelect && inputRef.current) inputRef.current.value = '';
        },
        [onPlaceChanged, clearOnSelect],
    );

    const googlePlacesRef = useGooglePlaces({ autoCompleteOptions, onPlaceChanged: handlePlaceChanged });

    // The hook accepts the wrapper and finds the input within it. We keep our own handle on that
    // input too, for clearOnSelect.
    const wrapperRef = useCallback(
        (node: HTMLDivElement | null) => {
            googlePlacesRef(node);
            inputRef.current = node?.querySelector('input') ?? null;
        },
        [googlePlacesRef],
    );

    return (
        <div
            ref={wrapperRef}
            className="flex h-12 items-center gap-3 rounded-[8px] border border-transparent bg-slate-grey-50 px-4 transition-colors hover:border-slate-grey-300"
        >
            <FontAwesomeIcon icon={faLocationDot} className="w-4 text-base text-slate-grey" />
            <input
                type="text"
                aria-label={ariaLabel}
                placeholder={placeholder}
                className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
            />
        </div>
    );
}
